import { useMemo, useState } from "react"
import { Loader2 } from "lucide-react"
import type { AppShell } from "@/App"
import { errorMessage, moveOrder, type MoveTarget, type Order, type PlanView, type Vehicle } from "@/lib/api"
import { day10, dayShort, eur, kg as fmtKg, n } from "@/lib/format"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { inputCls, Tag } from "@/components/kit"
import { cn } from "@/lib/utils"

interface Target {
  key: string
  target: MoveTarget
  label: string
  sub: string
  vehicle?: Vehicle
}

/**
 * Atribuição à mão de várias encomendas de uma vez (o cliente planeia sobretudo
 * à mão): escolhe o destino — veículo × dia ou transportadora — e as encomendas,
 * e aplica uma a uma (cada uma recalcula a rota no serviço de rotas, com
 * progresso). Na frota ficam fixadas; uma que falhe não trava as outras.
 *
 * Vem com o destino já escolhido (célula livre do quadro) ou com as encomendas
 * já escolhidas (seleção nas Encomendas).
 */
export function AssignDialog({
  shell,
  view,
  vehicles,
  candidates,
  preselected,
  presetTarget,
  onClose,
  onApplied,
}: {
  shell: AppShell
  view: PlanView
  vehicles: Vehicle[]
  /** Encomendas que se podem escolher. */
  candidates: Order[]
  preselected?: string[]
  presetTarget?: { vehicle_id: string; day: string }
  onClose: () => void
  onApplied: () => Promise<void> | void
}) {
  const plan = view.plan!
  const [picked, setPicked] = useState<Set<string>>(new Set(preselected ?? []))
  const days = useMemo(() => [...new Set([...plan.days.map((d) => day10(d)), ...(view.assignable_days ?? [])])].filter((d): d is string => Boolean(d)).sort(), [plan.days, view.assignable_days])

  const targets = useMemo<Target[]>(() => {
    const out: Target[] = []
    for (const day of days) {
      for (const v of vehicles.filter((x) => x.active !== false)) {
        const r = view.routes.find((x) => x.vehicle_id === v.id && day10(x.day) === day)
        if (r?.status === "confirmed") continue
        out.push({ key: `${v.id}:${day}`, target: { vehicle_id: v.id, day }, label: `${dayShort(day)} · ${v.name}`, sub: r ? `${r.stop_count} paragens · ${fmtKg(r.kg)}` : "não sai (abre a rota)", vehicle: v })
      }
    }
    out.push({ key: "carrier", target: "carrier", label: "Transportadora mais barata", sub: "a mais barata no prazo de cada encomenda" })
    const services = new Map<string, { label: string }>()
    for (const o of candidates) for (const c of o.options) if (c.ok && !services.has(c.service_id)) services.set(c.service_id, { label: `${c.carrier_name} · ${c.service_name}` })
    for (const [id, s] of services) out.push({ key: `svc:${id}`, target: { carrier_service_id: id }, label: s.label, sub: "só as encomendas que ela serve no prazo" })
    return out
  }, [days, vehicles, view.routes, candidates])

  const [targetKey, setTargetKey] = useState<string | null>(presetTarget ? `${presetTarget.vehicle_id}:${presetTarget.day}` : null)
  const target = targets.find((t) => t.key === targetKey) ?? null
  const toFleet = !!target && typeof target.target === "object" && "vehicle_id" in target.target
  const service = target && typeof target.target === "object" && "carrier_service_id" in target.target ? target.target.carrier_service_id : null

  /** Porque é que esta encomenda não pode ir para o destino escolhido (null = pode). */
  const blockedFor = (o: Order): string | null => {
    if (!target) return null
    if (toFleet) {
      if (o.geo_precision === "none" || o.lat == null) return "sem localização"
      if (o.needs_tail_lift && target.vehicle && !target.vehicle.has_tail_lift) return "pede plataforma"
      return null
    }
    if (service) {
      const c = o.options.find((x) => x.service_id === service)
      return !c ? "não serve" : !c.ok ? (c.why ?? "não serve") : null
    }
    return o.delivery_cost == null ? "sem transportadora no prazo" : null
  }

  // Pesquisa por localidade, CP (começa por), cliente ou número — para escolher, por exemplo, todas as de Coimbra.
  const [q, setQ] = useState("")
  // Por CP (aproxima as que ficam perto umas das outras) ou por prazo; só as que vencem até ao dia do destino.
  const [sort, setSort] = useState<"cp" | "due">("cp")
  const [dueOnly, setDueOnly] = useState(false)
  const targetDay = target && typeof target.target === "object" && "day" in target.target ? target.target.day : null
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const list = candidates.filter((o) => {
      if (dueOnly && targetDay && !(day10(o.due_date) && day10(o.due_date)! <= targetDay)) return false
      if (!needle) return true
      return [o.locality, o.municipality, o.district, o.customer_name, o.source_ref].some((v) => v?.toLowerCase().includes(needle)) || (o.postal_code ?? "").replace(/\s/g, "").startsWith(needle.replace(/\s/g, ""))
    })
    const key = (o: Order) => (sort === "due" ? `${day10(o.due_date) ?? "9999"}|${o.postal_code ?? ""}` : `${o.postal_code ?? "9999"}|${day10(o.due_date) ?? ""}`)
    return [...list].sort((x, y) => key(x).localeCompare(key(y)))
  }, [candidates, q, sort, dueOnly, targetDay])
  const chosen = candidates.filter((o) => picked.has(o.id))
  const runnable = chosen.filter((o) => !blockedFor(o))
  const selKg = chosen.reduce((a, o) => a + (n(o.total_kg) ?? 0), 0)
  const selM3 = chosen.reduce((a, o) => a + (n(o.total_m3) ?? 0), 0)
  // Carga da rota desse veículo nesse dia (se já sai) mais a escolhida, face à capacidade do veículo.
  const route = toFleet && target ? view.routes.find((r) => r.vehicle_id === (target.target as { vehicle_id: string }).vehicle_id && day10(r.day) === targetDay) ?? null : null
  const capKg = n(target?.vehicle?.max_kg) ?? 0
  const capM3 = n(target?.vehicle?.max_m3) ?? 0
  const loadKg = (n(route?.kg) ?? 0) + selKg
  const loadM3 = (n(route?.m3) ?? 0) + selM3

  const [progress, setProgress] = useState<string | null>(null)
  const [failures, setFailures] = useState<string[]>([])
  const apply = async () => {
    if (!target) return
    const failed: string[] = []
    let done = 0
    for (const o of runnable) {
      setProgress(`A atribuir ${done + 1}/${runnable.length}: ${o.source_ref}…`)
      try {
        await moveOrder(shell, o.id, target.target, false)
      } catch (err) {
        failed.push(`${o.source_ref}: ${errorMessage(err)}`)
      }
      done++
    }
    setProgress(null)
    setFailures(failed)
    const ok = runnable.length - failed.length
    if (ok) shell.toast("success", `${ok} encomenda(s) atribuída(s)`, target.label)
    await onApplied()
    if (!failed.length) onClose()
  }

  const toggle = (id: string) => setPicked((p) => { const next = new Set(p); if (next.has(id)) next.delete(id); else next.add(id); return next })

  return (
    <Dialog open onOpenChange={(v) => !v && !progress && onClose()}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Atribuir encomendas{target ? ` · ${target.label}` : ""}</DialogTitle>
        </DialogHeader>

        {!presetTarget && (
          <div>
            <div className="mb-1 text-[12px] font-medium text-muted-foreground">Destino</div>
            <div className="grid max-h-48 grid-cols-2 gap-1.5 overflow-y-auto sm:grid-cols-3">
              {targets.map((t) => (
                <button
                  key={t.key}
                  disabled={!!progress}
                  onClick={() => setTargetKey(t.key)}
                  className={cn("flex flex-col items-start rounded-lg border p-2 text-left text-[12px] hover:border-foreground/30", targetKey === t.key && "border-accent ring-1 ring-accent")}
                >
                  <b>{t.label}</b>
                  <span className="text-muted-foreground">{t.sub}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {toFleet && target?.vehicle && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-lg border bg-muted/30 px-3 py-2 text-[12px]">
            <span className="text-muted-foreground">{route ? `Já leva ${route.stop_count} paragens · ${fmtKg(route.kg)}` : "Ainda não sai neste dia"}</span>
            <Capacity label="kg" used={loadKg} cap={capKg} fmt={(v) => fmtKg(v)} />
            {capM3 > 0 ? <Capacity label="m³" used={loadM3} cap={capM3} fmt={(v) => `${v.toFixed(2).replace(".", ",")} m³`} /> : <span className="text-amber-700">volume do veículo por preencher</span>}
            <span className="text-muted-foreground">+ {chosen.length} escolhida(s)</span>
          </div>
        )}

        <div>
          <div className="mb-1 flex flex-wrap items-center justify-between gap-2 text-[12px] text-muted-foreground">
            <span className="font-medium">Encomendas · {visible.length} na lista · {chosen.length} escolhidas · {fmtKg(selKg)}</span>
            <span className="flex flex-wrap items-center gap-2">
              <input className={cn(inputCls, "h-7 w-72 text-[12px]")} placeholder="Localidade, concelho, distrito, CP, cliente ou nº…" value={q} onChange={(e) => setQ(e.target.value)} />
              <select className={cn(inputCls, "h-7 w-auto text-[12px]")} value={sort} onChange={(e) => setSort(e.target.value as "cp" | "due")} aria-label="Ordenar">
                <option value="cp">por código postal</option>
                <option value="due">por prazo</option>
              </select>
              {targetDay && (
                <label className="flex items-center gap-1">
                  <input type="checkbox" checked={dueOnly} onChange={(e) => setDueOnly(e.target.checked)} /> vencem até {dayShort(targetDay)}
                </label>
              )}
              <button className="underline" onClick={() => setPicked(new Set([...picked, ...visible.filter((o) => !blockedFor(o)).map((o) => o.id)]))}>{q || dueOnly ? "todas as da lista" : "todas"}</button>
              <button className="underline" onClick={() => setPicked(new Set())}>nenhuma</button>
            </span>
          </div>
          <div className="max-h-[52vh] overflow-y-auto rounded-lg border">
            <table className="w-full border-collapse text-[12.5px]">
              <thead className="sticky top-0 z-10 bg-background shadow-[0_1px_0_hsl(var(--border))]">
                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="w-8 px-2 py-1.5" />
                  <th className="px-2 py-1.5">Encomenda</th>
                  <th className="px-2 py-1.5">Cliente</th>
                  <th className="px-2 py-1.5">Destino</th>
                  <th className="px-2 py-1.5">Prazo</th>
                  <th className="px-2 py-1.5 text-right">Carga</th>
                  <th className="px-2 py-1.5 text-right">Transportadora</th>
                  <th className="px-2 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && (
                  <tr><td colSpan={8} className="p-3 text-sm text-muted-foreground">{candidates.length ? "Nenhuma encomenda com estes filtros." : "Não há encomendas por atribuir."}</td></tr>
                )}
                {visible.map((o) => {
                  const why = blockedFor(o)
                  const due = day10(o.due_date)
                  const late = !!targetDay && !!due && due < targetDay
                  return (
                    <tr key={o.id} className={cn("cursor-pointer border-t hover:bg-muted/40", picked.has(o.id) && "bg-accent/5", why && "opacity-60")} onClick={() => !progress && toggle(o.id)}>
                      <td className="px-2 py-1.5"><input type="checkbox" checked={picked.has(o.id)} disabled={!!progress} onChange={() => toggle(o.id)} onClick={(e) => e.stopPropagation()} aria-label={`Escolher ${o.source_ref}`} /></td>
                      <td className="px-2 py-1.5 font-mono text-xs">{o.source_ref}</td>
                      <td className="max-w-[16rem] truncate px-2 py-1.5">{o.customer_name ?? "—"}</td>
                      <td className="max-w-[16rem] truncate px-2 py-1.5">{o.locality ?? "—"} <span className="font-mono text-[11px] text-muted-foreground">{o.postal_code}</span></td>
                      <td className={cn("px-2 py-1.5 font-mono text-xs", late && "text-red-700")} title={late ? "O prazo é antes deste dia" : undefined}>{due ? dayShort(due) : "—"}</td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-right font-mono text-[11.5px]">{o.load_class ?? "?"} · {fmtKg(o.total_kg)}{o.weight_assumed ? " *" : ""}</td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-right font-mono text-[11.5px] text-muted-foreground">{o.delivery_cost != null ? eur(o.delivery_cost) : "—"}</td>
                      <td className="px-2 py-1.5">{why && <Tag tone="warn">{why}</Tag>}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {visible.some((o) => o.weight_assumed) && <div className="mt-1 text-[11px] text-muted-foreground">* peso assumido (artigo sem peso)</div>}
        </div>

        {progress && <div className="flex items-center gap-2 text-sm text-blue-700"><Loader2 className="h-4 w-4 animate-spin" /> {progress}</div>}
        {failures.length > 0 && (
          <ul className="list-disc rounded-md bg-red-50 py-1.5 pl-5 text-[12.5px] text-red-800">
            {failures.map((f) => <li key={f}>{f}</li>)}
          </ul>
        )}
        {toFleet && runnable.length > 0 && !progress && <div className="text-[12px] text-muted-foreground">Cada encomenda entra na posição da rota que menos tempo acrescenta e fica fixada nesse veículo e dia.</div>}

        <DialogFooter>
          <Button variant="outline" disabled={!!progress} onClick={onClose}>{failures.length ? "Fechar" : "Cancelar"}</Button>
          <Button disabled={!target || runnable.length === 0 || !!progress} onClick={apply}>
            Atribuir {runnable.length || ""}{chosen.length > runnable.length ? ` (${chosen.length - runnable.length} não podem)` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Barra de ocupação: verde até 85 %, âmbar até 100 %, vermelho acima. */
function Capacity({ label, used, cap, fmt }: { label: string; used: number; cap: number; fmt: (v: number) => string }) {
  const pct = cap > 0 ? used / cap : 0
  return (
    <span className="flex items-center gap-1.5">
      <span className="text-muted-foreground">{label}</span>
      <span className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
        <span className={cn("block h-full rounded-full", pct > 1 ? "bg-red-600" : pct > 0.85 ? "bg-amber-500" : "bg-green-600")} style={{ width: `${Math.min(100, pct * 100)}%` }} />
      </span>
      <span className={cn("font-mono", pct > 1 && "text-red-700")}>{fmt(used)} / {fmt(cap)}</span>
    </span>
  )
}
